import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...rest }, ref) => (
    <input
      ref={ref}
      className={cn(
        'h-10 w-full rounded-lg border bg-[var(--color-bg-input)] px-3 text-sm text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-subtle)] transition-colors',
        'border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-ink)] focus:outline-none',
        invalid && 'border-[var(--color-error)] focus:border-[var(--color-error)]',
        className,
      )}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'w-full rounded-lg border bg-[var(--color-bg-input)] px-3 py-2 text-sm text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-subtle)]',
        'border-[var(--color-border-default)] focus:border-[var(--color-accent-ink)] focus:outline-none',
        invalid && 'border-[var(--color-error)]',
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, children, ...rest }, ref) => (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full rounded-lg border bg-[var(--color-bg-input)] px-3 text-sm text-[var(--color-fg-primary)]',
        'border-[var(--color-border-default)] focus:border-[var(--color-accent-ink)] focus:outline-none',
        // Las <option> las pinta el navegador fuera del flujo normal: sin un
        // fondo SÓLIDO propio heredan la fuente del control sobre el popup del
        // sistema (blanco sobre blanco en la superficie night con tema claro).
        // bg-primary y no bg-input: el input es translúcido a propósito y un
        // rgba sobre el popup vuelve a depender del color de debajo.
        '[&_option]:bg-[var(--color-bg-primary)] [&_option]:text-[var(--color-fg-primary)]',
        invalid && 'border-[var(--color-error)]',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
